import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { formatPriceCents } from '@/lib/format';
import {
  type AlternateMatch,
  type AlternateStatus,
  type AttachAlternateRequest,
  useAttachAlternate,
  useFindAlternates,
  useFindSimilar,
} from '@/lib/findAlternates';
import { useSuppliers } from '@/lib/sourcing';

interface Props {
  orgSlug: string;
  /** The part the user is viewing — added suppliers attach to this id. */
  partId: number;
  /** Manufacturer PN to fan out across distributors. The drawer's
   * "Find alternates" button is disabled when the part has no MPN, so
   * this is always populated for MPN-mode searches. */
  mpn: string;
  manufacturer: string | null;
  /** The part's free-text description, used when the user toggles
   * the modal into description-search mode. May be null — the toggle
   * is disabled in that case. */
  description: string | null;
  onClose: () => void;
}

type SearchMode = 'mpn' | 'description';

type AttachState =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'added' }
  | { kind: 'error'; message: string };

const ROW_KEY = (m: AlternateMatch): string =>
  `${m.distributor_slug}::${m.distributor_pn ?? ''}`;

function statusPill(
  status: AlternateStatus,
  isSubstitute: boolean,
  isObsolete: boolean = false,
): JSX.Element {
  if (status === 'found') {
    if (isObsolete) {
      // The distributor knows the MPN but their listing is dead.
      // Distinct signal from "not carried" (vendor doesn't have it
      // at all) — useful when every distributor only stocks the
      // obsolete listing and the user wants to confirm the part is
      // truly gone everywhere.
      return (
        <span
          className="rounded bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-700 dark:text-red-400"
          title="Distributor lists this MPN as obsolete — not a viable replacement, just confirmation that the listing exists."
        >
          obsolete
        </span>
      );
    }
    if (isSubstitute) {
      // Amber instead of emerald: the distributor IS in catalog but
      // for a different MPN. Adding it as supplier should land in
      // the substitute confirmation flow (Phase 4) rather than the
      // exact-match path.
      return (
        <span
          className="rounded bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400"
          title="Vendor's MPN differs from what you searched — this is a substitute, not an exact match."
        >
          substitute
        </span>
      );
    }
    return (
      <span className="rounded bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
        in catalog
      </span>
    );
  }
  if (status === 'not_found') {
    return (
      <span className="rounded bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-stone-500 dark:text-stone-400">
        not carried
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="rounded bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-700 dark:text-red-400">
        error
      </span>
    );
  }
  return (
    <span className="rounded bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-stone-500 dark:text-stone-400">
      not configured
    </span>
  );
}

function formatStock(qty: number | null, inStock: boolean): string {
  if (qty === null) return inStock ? 'in stock' : '—';
  return qty.toLocaleString();
}

/** Mirror of the backend sanitizer for the editable search input's
 * default value. The backend re-runs sanitization defensively so this
 * is just for nicer UX — the user sees the cleaned query and can edit
 * it without first watching their punctuation get stripped. */
function sanitizeForDisplay(raw: string): string {
  let out = raw;
  out = out.replace(/[±™®]/g, ' ');
  out = out.replace(/[—–]/g, ' ');
  out = out.replace(/\([^)]*\)/g, ' ');
  out = out.replace(/[,;/]+/g, ' ');
  out = out.replace(/\s+/g, ' ').trim();
  if (out.length > 100) {
    const trimmed = out.slice(0, 100);
    const lastSpace = trimmed.lastIndexOf(' ');
    out = lastSpace > 0 ? trimmed.slice(0, lastSpace) : trimmed;
  }
  return out;
}

/**
 * Cross-distributor MPN fanout modal. Opens from the part drawer's
 * "Find alternates" button. Shows one row per org-enabled distributor
 * with the headline match (or a status placeholder), plus a per-row
 * "Add as supplier" button that re-fetches via the lookup adapter and
 * attaches the result as a PartSupplier on the current part.
 *
 * Layered above the drawer (z-50 vs drawer's z-40) so closing the
 * modal returns the user to the same part. Per-distributor "additional"
 * matches hide behind a per-distributor expander since most MPNs map
 * to one canonical product per vendor.
 */
export function FindAlternatesModal({
  orgSlug,
  partId,
  mpn,
  manufacturer,
  description,
  onClose,
}: Props) {
  const [mode, setMode] = useState<SearchMode>('mpn');
  const mpnFanout = useFindAlternates(orgSlug);
  const similarFanout = useFindSimilar(orgSlug);
  const fanout = mode === 'mpn' ? mpnFanout : similarFanout;
  const attach = useAttachAlternate(orgSlug);
  const canSearchByDescription =
    Boolean(description && description.trim().length > 0);
  // Existing suppliers — needed so the substitute-replace flow can
  // pass `prev_primary_supplier_id` for the server's race-guard
  // (rejects the request with 409 if the user's mental model of
  // "current primary" doesn't match reality).
  const existingSuppliers = useSuppliers(orgSlug, partId);
  // Per-row attach state — keyed by (slug, pn) so a click on one row
  // doesn't reset another's "Added ✓" pill.
  const [attachStates, setAttachStates] = useState<
    Record<string, AttachState>
  >({});
  // Per-distributor "show more" toggle for additional[].
  const [expandedSlugs, setExpandedSlugs] = useState<Set<string>>(new Set());
  // Phase 4: confirmation dialog state. When non-null, the modal
  // dims behind it and the dialog asks how to attach (alternate /
  // make primary / replace primary + update MPN).
  const [confirming, setConfirming] = useState<AlternateMatch | null>(null);

  // Description-mode editable query. Pre-filled with the part's
  // description (sanitized client-side for display); user can rewrite
  // before searching. Aaron's feedback: DigiKey-style descriptions
  // ("10000 pF ±5% 50V Ceramic Capacitor C0G, NP0 0603 (1608 Metric)")
  // tokenize poorly on Mouser/Newark — a hand-edited shorter query
  // ("10000pF 50V 0603 ceramic") consistently outperforms the literal
  // detailed_description.
  const defaultSimilarQuery = useMemo(
    () => sanitizeForDisplay(description ?? ''),
    [description],
  );
  const [similarQuery, setSimilarQuery] = useState(defaultSimilarQuery);

  // Fire the active fanout when the modal opens AND when the user
  // toggles modes. Each mutation's `data` is independent, so switching
  // back to MPN mode after running a description search shows the
  // first set's results from the cached state without a refetch.
  useEffect(() => {
    if (mode === 'mpn') {
      mpnFanout.mutate({ mpn, manufacturer: manufacturer ?? undefined });
    } else if (similarQuery.trim()) {
      similarFanout.mutate({ description: similarQuery.trim() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const fireSimilarSearch = () => {
    if (!similarQuery.trim()) return;
    similarFanout.mutate({ description: similarQuery.trim() });
  };

  const currentPrimaryId =
    existingSuppliers.data
      ?.slice()
      .sort((a, b) => a.priority - b.priority)[0]?.id ?? null;

  const performAttach = async (
    match: AlternateMatch,
    flags: Partial<AttachAlternateRequest>,
  ) => {
    if (!match.distributor_pn) return;
    const key = ROW_KEY(match);
    setAttachStates((prev) => ({ ...prev, [key]: { kind: 'pending' } }));
    try {
      await attach.mutateAsync({
        distributor_slug: match.distributor_slug,
        distributor_pn: match.distributor_pn,
        target_part_id: partId,
        ...flags,
      });
      setAttachStates((prev) => ({ ...prev, [key]: { kind: 'added' } }));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Attach failed';
      setAttachStates((prev) => ({
        ...prev,
        [key]: { kind: 'error', message },
      }));
    }
  };

  const handleAdd = async (match: AlternateMatch) => {
    if (!match.distributor_pn) return;
    // Phase 4: always prompt (per Aaron's design call). Open the
    // confirmation dialog rather than attaching silently. The dialog
    // adapts: exact-match rows get a 2-option choice; substitute rows
    // get the 3-option flow including "Replace primary + update MPN."
    setConfirming(match);
  };

  const toggleExpand = (slug: string) => {
    setExpandedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const data = fanout.data;
  const additionalCounts = useMemo(() => {
    if (!data) return {};
    return Object.fromEntries(
      Object.entries(data.additional).map(([slug, rows]) => [slug, rows.length]),
    );
  }, [data]);

  // Portal to document.body. The drawer's `translate-x` transform
  // creates a new containing block for `position: fixed` descendants,
  // so without this the modal positions relative to the drawer (which
  // can be scrolled / off-screen) instead of the viewport. Aaron hit
  // this when his drawer was scrolled to the Suppliers section and
  // the modal popped up "at the top of the drawer" — outside the
  // visible scroll area.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-xl">
        <div className="flex items-start justify-between border-b border-stone-200 dark:border-stone-800 px-5 py-3 gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">
              {mode === 'mpn' ? (
                <>
                  Alternates for <span className="font-mono">{mpn}</span>
                </>
              ) : (
                <>Similar to this part</>
              )}
            </h2>
            <p className="truncate text-xs text-stone-500 dark:text-stone-400">
              {mode === 'mpn'
                ? manufacturer
                  ? `${manufacturer} • cross-distributor MPN search`
                  : 'cross-distributor MPN search'
                : description
                  ? `Searching: "${description}"`
                  : 'Description-seeded replacement search'}
            </p>
          </div>
          <div className="flex items-start gap-2">
            <ModeToggle
              mode={mode}
              onChange={setMode}
              canSearchByDescription={canSearchByDescription}
            />
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {mode === 'description' && (
            <div className="mb-4 rounded-md border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/50 px-3 py-2.5">
              <label className="block text-xs font-medium text-stone-700 dark:text-stone-300 mb-1.5">
                Search query
              </label>
              <form
                className="flex flex-wrap items-stretch gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  fireSimilarSearch();
                }}
              >
                <input
                  type="text"
                  value={similarQuery}
                  onChange={(e) => setSimilarQuery(e.target.value)}
                  className="min-w-0 flex-1 rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-1.5 text-sm font-mono"
                  placeholder="e.g. 10000pF 50V 0603 ceramic"
                />
                <Button
                  type="submit"
                  disabled={
                    !similarQuery.trim() || similarFanout.isPending
                  }
                >
                  {similarFanout.isPending ? 'Searching…' : 'Search'}
                </Button>
              </form>
              <p className="mt-1.5 text-xs text-stone-500 dark:text-stone-400">
                Shorter, simpler queries usually work better than the
                full DigiKey-style description. Try the part's value,
                package, voltage, and key feature words.
              </p>
            </div>
          )}

          {fanout.isPending && (
            <p className="flex items-center gap-2 text-sm text-stone-500 dark:text-stone-400">
              <Spinner className="h-4 w-4 text-forge-600 dark:text-forge-400" />
              Searching every enabled distributor in parallel…
            </p>
          )}

          {fanout.isError && (
            <p className="text-sm text-red-600">
              Search failed: {(fanout.error as Error).message}
            </p>
          )}

          {data && data.results.length === 0 && (
            <p className="text-sm text-stone-500 dark:text-stone-400">
              No distributors enabled for this organization. Open
              distributor settings to enable at least one before searching
              for alternates.
            </p>
          )}

          {/* Soft fallback prompt: if MPN search came back with zero
              "found" rows (every distributor returned not_found / error
              / skipped), nudge the user toward description search. The
              banner sits above the table so the existing not_found
              rows are still visible — the user might still want to
              note which distributors don't carry the MPN. */}
          {mode === 'mpn'
            && data
            && data.results.length > 0
            && data.results.every((r) => r.status !== 'found')
            && canSearchByDescription && (
              <div className="mb-3 rounded border border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-900/20 px-3 py-2 text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  No distributor carries this MPN.
                </p>
                <p className="mt-1 text-xs text-amber-700/80 dark:text-amber-300/80">
                  The part may be discontinued everywhere. Try searching
                  by description to find candidate replacements.
                </p>
                <Button
                  variant="secondary"
                  className="mt-2"
                  onClick={() => setMode('description')}
                >
                  Search by description →
                </Button>
              </div>
            )}

          {data && data.results.length > 0 && (
            <>
              <p className="mb-3 text-xs text-stone-500 dark:text-stone-400">
                One row per enabled distributor. EOL listings are filtered
                out — a distributor showing "not carried" may stock
                obsolete-only.
              </p>
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-stone-500 dark:text-stone-400">
                  <tr>
                    <th className="pb-2 pr-3 text-left font-medium">
                      Distributor
                    </th>
                    <th className="pb-2 pr-3 text-left font-medium">
                      Their PN
                    </th>
                    <th className="pb-2 pr-3 text-left font-medium">MPN</th>
                    <th className="pb-2 pr-3 text-left font-medium">
                      Manufacturer
                    </th>
                    <th className="pb-2 pr-3 text-left font-medium">Stock</th>
                    <th className="pb-2 pr-3 text-left font-medium">
                      Price @ 1
                    </th>
                    <th className="pb-2 text-right font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.flatMap((match) => {
                    // Emit the headline row, then any expanded extras
                    // for that distributor, in one pass per
                    // distributor. The previous two-pass approach
                    // pushed *all* expanded extras to the bottom of
                    // the table, which made variants from one
                    // distributor visually appear under the next
                    // distributor's row. (Aaron's AR10001: clicking
                    // "Show 2 more from Mouser" rendered the rows
                    // below Newark.)
                    const rows = [
                      <Row
                        key={match.distributor_slug}
                        match={match}
                        attachState={attachStates[ROW_KEY(match)] ?? { kind: 'idle' }}
                        additionalCount={additionalCounts[match.distributor_slug] ?? 0}
                        isExpanded={expandedSlugs.has(match.distributor_slug)}
                        onAdd={() => handleAdd(match)}
                        onToggleExpand={() =>
                          toggleExpand(match.distributor_slug)
                        }
                      />,
                    ];
                    if (expandedSlugs.has(match.distributor_slug)) {
                      const extras =
                        data.additional[match.distributor_slug] ?? [];
                      for (const extra of extras) {
                        rows.push(
                          <Row
                            key={ROW_KEY(extra) + ':extra'}
                            match={extra}
                            isExtra
                            attachState={attachStates[ROW_KEY(extra)] ?? { kind: 'idle' }}
                            additionalCount={0}
                            isExpanded={false}
                            onAdd={() => handleAdd(extra)}
                            onToggleExpand={() => undefined}
                          />,
                        );
                      }
                    }
                    return rows;
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 px-5 py-3">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>

      {confirming && (
        <AddAsSupplierConfirmDialog
          match={confirming}
          partMpn={mpn}
          currentPrimaryId={currentPrimaryId}
          // In description-search mode every result is by definition a
          // candidate replacement (different MPN, different part). The
          // confirmation dialog should treat them all as substitutes
          // even though the backend's `is_substitute` is False on
          // description-mode rows (it has to be — there's no canonical
          // MPN to compare against).
          forceSubstitute={mode === 'description'}
          onCancel={() => setConfirming(null)}
          onConfirm={async (flags) => {
            const target = confirming;
            setConfirming(null);
            await performAttach(target, flags);
          }}
        />
      )}
    </div>,
    document.body,
  );
}


/** Two-button toggle in the modal header that switches between MPN
 * and description seed. The description button is disabled when the
 * part has no description text — there's nothing to search on, and
 * surfacing a non-functional control would just confuse. */
function ModeToggle({
  mode,
  onChange,
  canSearchByDescription,
}: {
  mode: SearchMode;
  onChange: (next: SearchMode) => void;
  canSearchByDescription: boolean;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-stone-300 dark:border-stone-700 text-xs">
      <button
        type="button"
        onClick={() => onChange('mpn')}
        className={cn(
          'px-2.5 py-1 transition-colors',
          mode === 'mpn'
            ? 'bg-forge-600 text-white'
            : 'bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800',
        )}
        aria-pressed={mode === 'mpn'}
      >
        By MPN
      </button>
      <button
        type="button"
        onClick={() => onChange('description')}
        disabled={!canSearchByDescription}
        title={
          canSearchByDescription
            ? 'Search using the part description'
            : 'Part has no description to search on'
        }
        className={cn(
          'px-2.5 py-1 border-l border-stone-300 dark:border-stone-700 transition-colors',
          mode === 'description'
            ? 'bg-forge-600 text-white'
            : 'bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
        aria-pressed={mode === 'description'}
      >
        By description
      </button>
    </div>
  );
}


/** Phase 4: confirmation dialog rendered when the user clicks "Add
 * as supplier" on any found row. Aaron's design call: always prompt,
 * even on exact-MPN matches, since users coming to find-alternates
 * are usually trying to source-replace a broken supplier and the
 * "should this become primary?" question has no good silent default.
 *
 * Dialog content adapts based on whether the row is a substitute:
 * - Exact match: 2 options (Add as alternate / Make primary).
 * - Substitute: 3 options (Add as alternate keeping the part's MPN /
 *   Replace primary AND update the part's MPN to match the
 *   substitute / Cancel). The MPN diff is shown prominently so the
 *   user sees they're picking a different part.
 *
 * Layered above the FindAlternatesModal (which is z-50) at z-60 so
 * dialog actions don't bleed through to the table behind. */
function AddAsSupplierConfirmDialog({
  match,
  partMpn,
  currentPrimaryId,
  forceSubstitute = false,
  onCancel,
  onConfirm,
}: {
  match: AlternateMatch;
  /** The part's current MPN (what the user typed into find-alternates). */
  partMpn: string;
  /** id of the part's current priority-1 supplier; needed by the
   * server's race-guard when update_part_mpn is true. Null when the
   * part has no suppliers at all (the dialog still opens but the
   * "Replace primary" option degrades — see below). */
  currentPrimaryId: number | null;
  /** When true, treat the match as a substitute regardless of the
   * server's `is_substitute` flag. Used by description-search results
   * where every row is implicitly a candidate replacement. */
  forceSubstitute?: boolean;
  onCancel: () => void;
  onConfirm: (flags: Partial<AttachAlternateRequest>) => Promise<void>;
}) {
  const isSubstitute = match.is_substitute || forceSubstitute;
  // The "Replace primary + update MPN" option needs the current
  // primary's id for the server's race-guard. If for some reason
  // the part has no suppliers (the user landed in find-alternates
  // somehow without one), we hide that option — there's nothing to
  // replace, and "Make primary" with no other suppliers is
  // semantically the same as "alternate" (it'll be priority 1
  // either way).
  const canReplacePrimary = currentPrimaryId !== null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-900/50 p-4">
      <div className="flex w-full max-w-md flex-col overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-xl">
        <div className="border-b border-stone-200 dark:border-stone-800 px-5 py-3">
          <h3 className="text-base font-semibold">
            Add {match.distributor_name} supplier
          </h3>
          <p className="mt-0.5 text-xs text-stone-500 dark:text-stone-400">
            <span className="font-mono">{match.distributor_pn}</span>
          </p>
        </div>

        <div className="px-5 py-4 text-sm">
          {isSubstitute ? (
            <div className="rounded border border-amber-200 dark:border-amber-900/60 bg-amber-50/50 dark:bg-amber-900/20 px-3 py-2 mb-3">
              <p className="font-medium text-amber-800 dark:text-amber-200">
                This is a substitute, not the same MPN
              </p>
              <p className="mt-1 text-xs text-amber-700/80 dark:text-amber-300/80">
                Your part's MPN:{' '}
                <span className="font-mono">{partMpn}</span>
                <br />
                Vendor's MPN:{' '}
                <span className="font-mono">{match.mpn ?? '?'}</span>
              </p>
              <p className="mt-1 text-xs text-amber-700/80 dark:text-amber-300/80">
                These are related parts but not interchangeable
                without verifying the spec differences. Pick how you
                want to handle it:
              </p>
            </div>
          ) : (
            <p className="mb-3 text-stone-600 dark:text-stone-400 text-sm">
              How should this supplier sit relative to the part's
              existing suppliers?
            </p>
          )}

          <div className="space-y-2">
            <button
              type="button"
              onClick={() => {
                void onConfirm({});
              }}
              className="w-full text-left rounded border border-stone-200 dark:border-stone-700 px-3 py-2 hover:bg-stone-50 dark:hover:bg-stone-800"
            >
              <span className="font-medium">Add as alternate</span>
              <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                Append at the bottom of the supplier list. The
                current primary stays primary.
                {isSubstitute && ' Your part keeps its current MPN.'}
              </p>
            </button>

            {!isSubstitute && (
              <button
                type="button"
                onClick={() => {
                  void onConfirm({ make_primary: true });
                }}
                className="w-full text-left rounded border border-forge-300 dark:border-forge-700 bg-forge-50/50 dark:bg-forge-900/20 px-3 py-2 hover:bg-forge-100 dark:hover:bg-forge-900/40"
              >
                <span className="font-medium">Make this the primary supplier</span>
                <p className="text-xs text-stone-600 dark:text-stone-400 mt-0.5">
                  Promote to priority 1; the current primary shifts
                  down. The part's MPN is unchanged.
                </p>
              </button>
            )}

            {isSubstitute && canReplacePrimary && (
              <button
                type="button"
                onClick={() => {
                  void onConfirm({
                    make_primary: true,
                    update_part_mpn: true,
                    prev_primary_supplier_id: currentPrimaryId ?? undefined,
                  });
                }}
                className="w-full text-left rounded border border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/20 px-3 py-2 hover:bg-amber-100 dark:hover:bg-amber-900/40"
              >
                <span className="font-medium">
                  Replace primary AND update part MPN
                </span>
                <p className="text-xs text-stone-600 dark:text-stone-400 mt-0.5">
                  New supplier becomes primary. Old primary stays as
                  an alternate but flagged EOL (preserves residual-
                  stock tracking). The part's MPN updates from{' '}
                  <span className="font-mono">{partMpn}</span> →{' '}
                  <span className="font-mono">{match.mpn}</span>.
                </p>
              </button>
            )}

            {isSubstitute && !canReplacePrimary && (
              <button
                type="button"
                onClick={() => {
                  void onConfirm({ make_primary: true });
                }}
                className="w-full text-left rounded border border-forge-300 dark:border-forge-700 bg-forge-50/50 dark:bg-forge-900/20 px-3 py-2 hover:bg-forge-100 dark:hover:bg-forge-900/40"
              >
                <span className="font-medium">Make this the primary supplier</span>
                <p className="text-xs text-stone-600 dark:text-stone-400 mt-0.5">
                  No existing primary to replace. Part MPN unchanged.
                </p>
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 px-5 py-3">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}


interface RowProps {
  match: AlternateMatch;
  attachState: AttachState;
  /** Count of additional matches available for this distributor. 0 hides
   * the expander. Always 0 for `isExtra` rows. */
  additionalCount: number;
  isExpanded: boolean;
  isExtra?: boolean;
  onAdd: () => void;
  onToggleExpand: () => void;
}

function Row({
  match,
  attachState,
  additionalCount,
  isExpanded,
  isExtra = false,
  onAdd,
  onToggleExpand,
}: RowProps) {
  const isFound = match.status === 'found';
  return (
    <tr
      className={cn(
        'border-t border-stone-100 dark:border-stone-800',
        isExtra && 'bg-stone-50/50 dark:bg-stone-800/30',
      )}
    >
      <td className="py-2 pr-3">
        <div className="flex items-center gap-2">
          {!isExtra && <span className="font-medium">{match.distributor_name}</span>}
          {isExtra && (
            <span className="pl-4 text-xs text-stone-500 dark:text-stone-400">
              ↳ variant
            </span>
          )}
          {!isExtra
            && statusPill(match.status, match.is_substitute, match.is_obsolete)}
          {isExtra && match.is_obsolete && (
            <span className="rounded bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-700 dark:text-red-400">
              obsolete
            </span>
          )}
        </div>
        {match.error_message && (
          <p className="mt-0.5 text-xs text-stone-500 dark:text-stone-400">
            {match.error_message}
          </p>
        )}
        {!isExtra && additionalCount > 0 && (
          <button
            type="button"
            onClick={onToggleExpand}
            className="mt-0.5 text-xs text-forge-600 dark:text-forge-400 hover:underline"
          >
            {isExpanded
              ? `Hide ${additionalCount} more`
              : `Show ${additionalCount} more from ${match.distributor_name}`}
          </button>
        )}
      </td>
      <td className="py-2 pr-3 font-mono text-xs">
        {match.distributor_pn ? (
          match.supplier_url ? (
            <a
              href={match.supplier_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-forge-600 dark:text-forge-400 hover:underline"
            >
              {match.distributor_pn}
            </a>
          ) : (
            match.distributor_pn
          )
        ) : (
          <span className="text-stone-400 dark:text-stone-600">—</span>
        )}
      </td>
      <td className="py-2 pr-3 font-mono text-xs">
        {match.mpn ? (
          // Substitute rows: highlight the MPN in amber so the
          // user immediately sees that this is NOT the part they
          // searched for. Tooltip shows the side-by-side diff.
          match.is_substitute ? (
            <span
              className="text-amber-700 dark:text-amber-400"
              title={
                match.searched_mpn
                  ? `Searched: ${match.searched_mpn}\nVendor: ${match.mpn}`
                  : `Vendor MPN: ${match.mpn}`
              }
            >
              {match.mpn}
            </span>
          ) : (
            <span className="text-stone-700 dark:text-stone-300">{match.mpn}</span>
          )
        ) : (
          <span className="text-stone-400 dark:text-stone-600">—</span>
        )}
      </td>
      <td className="py-2 pr-3 text-stone-700 dark:text-stone-300">
        {match.manufacturer ?? '—'}
      </td>
      <td className="py-2 pr-3">
        {isFound ? (
          <span
            className={cn(
              match.in_stock
                ? 'text-emerald-700 dark:text-emerald-400'
                : 'text-stone-500 dark:text-stone-400',
            )}
          >
            {formatStock(match.stock_qty, match.in_stock)}
          </span>
        ) : (
          <span className="text-stone-400 dark:text-stone-600">—</span>
        )}
      </td>
      <td className="py-2 pr-3 text-stone-700 dark:text-stone-300">
        {isFound
          ? formatPriceCents(match.price_unit_cents, match.currency)
          : '—'}
      </td>
      <td className="py-2 text-right">
        {isFound ? (
          <ActionButton state={attachState} onClick={onAdd} />
        ) : (
          <span className="text-xs text-stone-400 dark:text-stone-600">—</span>
        )}
      </td>
    </tr>
  );
}


function ActionButton({
  state,
  onClick,
}: {
  state: AttachState;
  onClick: () => void;
}): JSX.Element {
  if (state.kind === 'added') {
    return (
      <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
        Added ✓
      </span>
    );
  }
  if (state.kind === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-stone-500 dark:text-stone-400">
        <Spinner className="h-3 w-3" />
        Adding…
      </span>
    );
  }
  if (state.kind === 'error') {
    return (
      <button
        type="button"
        onClick={onClick}
        className="text-xs text-red-600 dark:text-red-400 hover:underline"
        title={state.message}
      >
        Retry
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded border border-forge-300 dark:border-forge-700 px-2 py-0.5 text-xs font-medium text-forge-700 dark:text-forge-300 hover:bg-forge-50 dark:hover:bg-forge-900/30"
    >
      Add as supplier
    </button>
  );
}
